import { useEffect, useState } from 'react'
import type { JobNote, NoteEntityType, NoteVisibility } from '@workflow/contracts'
import { Button, cn, Dialog, EmptyNote, IconButton, Icons, Loading, StateNote, StatusLabel } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useAddNote, useNotes, useUpdateNote } from '@/lib/api/hooks'
import { formatRelative, formatTimestamp } from '@/lib/format'
import { Markdown } from '@/features/announcements/Markdown'
import { WritingSurface } from '@/features/writing/WritingSurface'

const VISIBILITY: Record<NoteVisibility, { label: string; tone: 'done' | 'idle' }> = {
  internal: { label: 'Internal', tone: 'idle' },
  shared: { label: 'Shared', tone: 'done' },
}

/**
 * The thread of notes on an entity. Visibility is the access decision:
 * `internal` never leaves staff, `shared` is what the player's portal will
 * read. A note is corrected, not removed — its edit is part of the thread.
 * Writing is not furniture: the composer lives up in the page's header as a
 * quiet line, and only the thread gathers here.
 */
export function JobNotes({
  entityType,
  entityId,
  emptyDetail = 'Use the box above to write the first note.',
}: {
  entityType: NoteEntityType
  entityId: string
  emptyDetail?: string
}) {
  const notes = useNotes(entityType, entityId)

  return (
    <div className="flex flex-col gap-4">
      {notes.isLoading ? (
        <Loading label="Loading notes" />
      ) : notes.error ? (
        <StateNote title="The notes did not load." detail={describeError(notes.error)} />
      ) : (notes.data?.length ?? 0) === 0 ? (
        <EmptyNote title="No notes yet." detail={emptyDetail} />
      ) : (
        <ul className="flex flex-col">
          {notes.data!.map((note) => (
            <NoteRow key={note.id} note={note} entityType={entityType} entityId={entityId} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Where a note is written from a page: one quiet line under the title that
 * opens the writing window — or, on the job page, the window alone, opened
 * from the Actions menu (`hideTrigger`, `open` and `onOpenChange`).
 */
export function JobNoteComposer({
  entityType,
  entityId,
  className,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: {
  entityType: NoteEntityType
  entityId: string
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen

  return (
    <div className={cn('scroll-mt-24', className)}>
      {hideTrigger ? null : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-caption text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
        >
          <Icons.plus />
          Write a note…
        </button>
      )}
      <NoteWindow open={open} onClose={() => setOpen(false)} entityType={entityType} entityId={entityId} editing={null} />
    </div>
  )
}

/**
 * Writing a note the way an announcement is written: the words on a page
 * with the shared marks at the foot, and who may read it as one switch
 * beside Post — Internal never leaves the studio, Shared reads on the
 * player's portal. A note starts internal, because a note written in the
 * studio's voice stays in the studio until someone decides otherwise.
 * Editing opens the same window with the note already in it.
 */
function NoteWindow({
  open,
  onClose,
  entityType,
  entityId,
  editing,
}: {
  open: boolean
  onClose: () => void
  entityType: NoteEntityType
  entityId: string
  editing: JobNote | null
}) {
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<NoteVisibility>('internal')
  const add = useAddNote()
  const update = useUpdateNote()

  useEffect(() => {
    if (!open) return
    setBody(editing?.body ?? '')
    setVisibility(editing?.visibility ?? 'internal')
    add.reset()
    update.reset()
  }, [open, editing])

  const busy = add.isPending || update.isPending
  const changed = editing === null || body.trim() !== editing.body || visibility !== editing.visibility
  const ready = body.trim().length > 0 && changed
  const fault = add.error ?? update.error
  const post = () => {
    if (!ready || busy) return
    if (editing) {
      update.mutate(
        {
          id: editing.id,
          input: {
            ...(body.trim() !== editing.body ? { body: body.trim() } : {}),
            ...(visibility !== editing.visibility ? { visibility } : {}),
          },
        },
        { onSuccess: onClose },
      )
    } else {
      add.mutate({ entityType, entityId, body: body.trim(), visibility }, { onSuccess: onClose })
    }
  }
  const shared = visibility === 'shared'

  return (
    <Dialog open={open} onClose={onClose} labelledBy="write-note-label" className="sm:max-w-2xl">
      <form
        className="flex max-h-[calc(100dvh-4rem)] flex-col rounded-3xl border bg-surface shadow-modal"
        onSubmit={(event) => {
          event.preventDefault()
          post()
        }}
      >
        <div className="flex items-center justify-between px-6 pt-5 sm:px-8">
          <p id="write-note-label" className="text-micro text-muted-foreground uppercase">
            {editing ? 'Edit Note' : 'New Note'}
          </p>
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom">
            <Icons.close />
          </IconButton>
        </div>
        <WritingSurface
          key={open ? 'open' : 'closed'}
          value={body}
          onChange={setBody}
          onSubmit={post}
          label="Note"
          placeholder="What the next person here needs to know…"
          maxLength={4000}
          minHeight="min-h-[10rem]"
          autoFocus
          panels={fault ? (
            <p key="fault" className="border-t border-hairline px-6 py-2.5 text-caption text-danger sm:px-8">{describeError(fault)}</p>
          ) : null}
          actions={
            <>
              <button
                type="button"
                aria-pressed={shared}
                title={shared ? 'Shared: the player reads this on their portal' : 'Internal: never leaves the studio'}
                onClick={() => setVisibility(shared ? 'internal' : 'shared')}
                className={cn(
                  'mr-1 inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-caption transition-colors duration-fast outline-none hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground',
                  shared ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {shared ? <Icons.shared size="sm" /> : <Icons.private size="sm" />}
                {shared ? 'Shared with the player' : 'Internal'}
              </button>
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="primary" size="sm" busy={busy} disabled={!ready}>
                {editing ? 'Save' : 'Post'}
              </Button>
            </>
          }
        />
      </form>
    </Dialog>
  )
}

function NoteRow({ note, entityType, entityId }: { note: JobNote; entityType: NoteEntityType; entityId: string }) {
  const [editing, setEditing] = useState(false)
  const shown = VISIBILITY[note.visibility]

  return (
    <li className="border-b border-hairline py-3 last:border-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-ui font-medium text-foreground">{note.authorName ?? 'Staff'}</span>
        <span className="text-caption text-muted-foreground" title={formatTimestamp(note.updatedAt)}>
          {note.updatedAt !== note.createdAt ? 'edited · ' : ''}
          {formatRelative(note.createdAt)}
        </span>
        <StatusLabel tone={shown.tone} label={shown.label} />
        <span className="ml-auto">
          <Button size="xs" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>
        </span>
      </div>
      <Markdown source={note.body} className="mt-1.5 max-w-[72ch] text-ui leading-relaxed text-foreground" />
      <NoteWindow open={editing} onClose={() => setEditing(false)} entityType={entityType} entityId={entityId} editing={note} />
    </li>
  )
}
